import {
  arrowLeftIcon,
  bankTransfer,
  defaultCoin,
  editIconBlack,
  helpIcon,
} from "~/assets/assets";
import {
  goBack,
  HubbleRoute,
  PageAlignment,
  pushPage,
} from "~/shared_states/modal";
import { ButtonRegular } from "~/widgets/button";
import { HowToTransferModal } from "./how_to_transfer_modal";
import { SummaryTransferModal } from "./summary_transfer_modal";
import { BankTransferEditAmountModal } from "./edit_amount_modal";
import { toRupees } from "~/utils/number";
import { useClassicContext } from "~/data/classic_context";
import { Accessor, createSignal, onMount } from "solid-js";
import { clientRepo } from "~/server/apis/client_repo";
import { CoinPayoutSimulateResponse } from "~/server/types/payout";
import { SeeWhyBS } from "./see_why_bs";

export function BankTransferDetails() {
  const context = useClassicContext();
  const contextData = context.data;
  const v4Home = contextData.isLoggedIn ? contextData.v4HomePage : null;
  const tdsPercentage = contextData.isLoggedIn
    ? (contextData.clientConfig?.tdsConfig?.percentage ?? 0)
    : 0;

  const [amount, setAmount] = createSignal(
    Number(
      ((v4Home?.coins.totalAvailable ?? 0) / (1 + tdsPercentage / 100)).toFixed(
        2
      )
    )
  );
  const [simulateResponse, setSimulateResponse] =
    createSignal<CoinPayoutSimulateResponse | null>(null);

  const simulatePayout = async (amount: number) => {
    try {
      const response = await clientRepo.simulatePayout({ amount });
      setSimulateResponse(response);
    } catch (error) {
      console.error("Failed to simulate payout:", error);
    }
  };

  onMount(() => {
    simulatePayout(amount());
  });

  return (
    <div class="relative flex h-full w-full flex-col items-center justify-start bg-baseTertiaryLight">
      <div class="flex w-full items-center justify-between px-4 py-3">
        <img
          class="h-6 w-6"
          src={arrowLeftIcon}
          alt=""
          onClick={() => goBack()}
        />
        <img
          class=" h-6 w-6"
          src={helpIcon}
          alt="how to transfer"
          onClick={() => {
            pushPage(
              () => <HowToTransferModal />,
              {
                isModal: true,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_HOW_TO_TRANSFER
            );
          }}
        />
      </div>
      <div class="flex w-full flex-col items-center justify-start ">
        <img src={bankTransfer} class="my-4 h-16 w-16" alt="bank transfer" />
        <h1 class="mb-3 text-h5 text-textDark">Transfer incentive to bank</h1>
        <div
          onClick={() => {
            pushPage(
              () => (
                <BankTransferEditAmountModal
                  initialAmount={amount()}
                  onAmountChange={(newAmount) => {
                    setAmount(newAmount);
                    simulatePayout(newAmount);
                  }}
                />
              ),
              {
                isModal: true,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_EDIT_AMOUNT
            );
          }}
          class="flex cursor-pointer items-center justify-end gap-1"
        >
          <p class="text-h1 text-textDark">{toRupees(amount())}</p>
          <img src={editIconBlack} alt="edit" />
        </div>
        <BankTransferDetailsCard
          amount={amount()}
          simulateResponse={simulateResponse}
        />
      </div>
      <ButtonRegular
        isEnabled={true}
        onClick={() => {
          if (amount() <= (simulateResponse()?.maxCalculatedInrPayout ?? 0)) {
            pushPage(
              () => (
                <SummaryTransferModal
                  type="payout"
                  amount={amount()}
                  redeemAmount={
                    simulateResponse()?.totalCoinsForRequestedAmount ?? 0
                  }
                  taxAmount={
                    simulateResponse()?.tdsCoinsForRequestedAmount ?? 0
                  }
                />
              ),
              {
                isModal: true,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_SUMMARY_TRANSFER
            );
          } else {
            pushPage(
              () => (
                <SeeWhyBS
                  amount={amount() ?? 0}
                  balance={simulateResponse()?.maxCalculatedInrPayout ?? 0}
                  redeemAmount={
                    simulateResponse()?.totalCoinsForRequestedAmount ?? 0
                  }
                  taxAmount={
                    simulateResponse()?.tdsCoinsForRequestedAmount ?? 0
                  }
                />
              ),
              {
                isModal: true,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_SEE_WHY_BS
            );
          }
        }}
        class="absolute bottom-4 left-4 right-4 mt-5 !w-[92%] "
      >
        Continue
      </ButtonRegular>
    </div>
  );
}

function BankTransferDetailsCard(props: {
  amount: number;
  simulateResponse: Accessor<CoinPayoutSimulateResponse | null>;
}) {
  const context = useClassicContext();
  const contextData = context.data;
  const v4Home = contextData.isLoggedIn ? contextData.v4HomePage : null;

  return (
    <div class="my-5 flex h-[58px] items-center justify-between gap-2 rounded-xl bg-baseTertiaryMedium px-4 py-3">
      <img src={defaultCoin} class="h-[25px] w-[25px]" alt="coin" />
      <div class="w-[154px]">
        <p class="text-mediumSemiBold text-textDark">
          Using{" "}
          <span class="text-coinPercentageOrange">
            {toRupees(
              props.simulateResponse()?.totalCoinsForRequestedAmount ?? 0
            )}
          </span>{" "}
          incentive
        </p>
        <p class="text-small">
          Balance: {toRupees(v4Home?.coins.totalAvailable ?? 0)}
        </p>
      </div>
      <p
        onClick={() => {
          pushPage(
            () => (
              <SummaryTransferModal
                type="payout"
                amount={props.amount}
                redeemAmount={
                  props.simulateResponse()?.totalCoinsForRequestedAmount ?? 0
                }
                taxAmount={
                  props.simulateResponse()?.tdsCoinsForRequestedAmount ?? 0
                }
              />
            ),
            {
              isModal: true,
              alignment: PageAlignment.bottom,
              offset: { top: 20, right: 20 },
            },
            HubbleRoute.BANK_SUMMARY_TRANSFER
          );
        }}
        class="items-baseline self-end text-[10px] font-semibold uppercase tracking-[-0.1px] text-textDark underline [text-underline-position:from-font]"
      >
        192(R) TAX adjusted
      </p>
    </div>
  );
}
